import { useEffect } from 'react';
import { useSiteSettings } from '@/hooks/useSiteSettings';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
  noIndex?: boolean;
}

export function SEO({
  title,
  description,
  keywords,
  ogImage,
  ogType = 'website',
  canonical,
  noIndex = false,
}: SEOProps) {
  const { data: settings } = useSiteSettings();

  useEffect(() => {
    const siteName = settings?.site_name || 'Din AI Bedrift';
    const titleSuffix = settings?.meta_defaults?.title_suffix || ` | ${siteName}`;
    const defaultDescription = settings?.meta_defaults?.default_description || settings?.site_description || '';

    // Set title
    const fullTitle = title ? `${title}${titleSuffix}` : siteName;
    document.title = fullTitle;

    // Helper function to set meta tag
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Set description
    const metaDescription = description || defaultDescription;
    if (metaDescription) {
      setMetaTag('description', metaDescription);
      setMetaTag('og:description', metaDescription, true);
      setMetaTag('twitter:description', metaDescription);
    }

    // Set keywords
    if (keywords) {
      setMetaTag('keywords', keywords);
    }

    // Set Open Graph tags
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:site_name', siteName, true);

    if (ogImage) {
      setMetaTag('og:image', ogImage, true);
      setMetaTag('twitter:image', ogImage);
    }

    // Set Twitter card
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', fullTitle);

    // Set canonical URL
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonical;
    }

    // Set robots meta
    if (noIndex) {
      setMetaTag('robots', 'noindex, nofollow');
    } else {
      setMetaTag('robots', 'index, follow');
    }
  }, [title, description, keywords, ogImage, ogType, canonical, noIndex, settings]);

  return null;
}
